import Logger from "../../../core/internal/logger"
import { WEBVIEW_BRIDGE_COMPLETION_TIMEOUT_MILLIS } from "../../../config"
import InvocationResponseResolver from "./InvocationResponseResolver"
import ErrorUtil from "../../../core/internal/util/ErrorUtil"

export interface HackleBridge {
  resolveMessage: (messageId: string, responseJson: string) => void
}

declare global {
  interface Window {
    _hackleBridge?: HackleBridge
  }
}

interface PendingRequest {
  onResponse: (responseJson: string) => void
  onError: (error: Error) => void
  onCancel: () => void
  timer: ReturnType<typeof setTimeout>
}

const pendingRequests = new Map<string, PendingRequest>()

function dispatch(messageId: string, responseJson: string): void {
  const pendingRequest = pendingRequests.get(messageId)
  if (!pendingRequest) {
    Logger.log.debug(`No pending bridge message. [messageId=${messageId}]`)
    return
  }

  pendingRequests.delete(messageId)
  clearTimeout(pendingRequest.timer)
  pendingRequest.onResponse(responseJson)
}

export function installHackleBridge(_window: Window): void {
  const existingBridge = _window._hackleBridge
  if (existingBridge && existingBridge.resolveMessage === dispatch) {
    return
  }

  _window._hackleBridge = { ...(existingBridge ?? {}), resolveMessage: dispatch }
}

export class BridgeResponseDispatcher {
  constructor(
    private readonly responseResolver: InvocationResponseResolver,
    private readonly timeoutMillis: number = WEBVIEW_BRIDGE_COMPLETION_TIMEOUT_MILLIS
  ) {}

  register(messageId: string): Promise<void> {
    return new Promise<void>((complete, fail) => {
      const previousRequest = pendingRequests.get(messageId)
      if (previousRequest) {
        clearTimeout(previousRequest.timer)
        previousRequest.onError(new Error(`Bridge request already pending. [messageId=${messageId}]`))
      }

      const timer = setTimeout(() => {
        pendingRequests.delete(messageId)
        Logger.log.debug(`Bridge request timed out. [messageId=${messageId}]`)
        complete()
      }, this.timeoutMillis)

      pendingRequests.set(messageId, {
        onResponse: (responseJson) => {
          try {
            this.responseResolver.resolve(responseJson)
            complete()
          } catch (e) {
            fail(ErrorUtil.asError(e))
          }
        },
        onError: fail,
        onCancel: () => {
          Logger.log.debug(`Bridge request canceled. [messageId=${messageId}]`)
          complete()
        },
        timer
      })
    })
  }

  cancel(messageId: string): void {
    const pendingRequest = pendingRequests.get(messageId)
    if (!pendingRequest) {
      Logger.log.debug(`Unknown bridge messageId on cancel: ${messageId}`)
      return
    }

    pendingRequests.delete(messageId)
    clearTimeout(pendingRequest.timer)
    pendingRequest.onCancel()
  }
}
